import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import TelemetryService from '../shared/services/TelemetryService';
import { EventType } from '../shared/services/TelemetryService.types';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';

// Configuração para chunks
const CHUNK_SIZE = 1024 * 1024; // 1MB por chunk

// Interface para os metadados do upload
interface VideoUploadMetadata {
  fileId: string;
  fileName: string;
  mimeType: string;
  totalSize: number;
  totalChunks: number;
  checksum: string;
}

// Interface para progresso do upload
interface UploadProgress {
  totalBytes: number;
  uploadedBytes: number;
  percentage: number;
  currentChunk: number;
  totalChunks: number;
}

/**
 * Serviço para upload de vídeos com suporte a chunks e resiliência
 */
class VideoUploadService {
  private static instance: VideoUploadService;
  private uploadQueue: Map<string, { blob: Blob, metadata: any, retries: number }> = new Map();
  private isProcessingQueue = false;
  private uploadInProgress: boolean = false;

  private constructor() {
    // Configurar axiosRetry para automaticamente tentar novamente em caso de falhas de rede
    axiosRetry(axios, {
      retries: 3,
      retryDelay: (retryCount) => {
        // Backoff exponencial: 1s, 2s, 4s, etc.
        return retryCount * 1000;
      },
      retryCondition: (error) => {
        // Repetir em erros de rede ou 5xx
        return axiosRetry.isNetworkOrIdempotentRequestError(error) || 
               (error.response?.status ? error.response.status >= 500 : false);
      }
    });

    // Iniciar processamento da fila ao conectar
    window.addEventListener('online', this.processQueue.bind(this));
  }

  public static getInstance(): VideoUploadService {
    if (!VideoUploadService.instance) {
      VideoUploadService.instance = new VideoUploadService();
    }
    return VideoUploadService.instance;
  }

  /**
   * Calcula o checksum MD5 de um Blob
   */
  private async calculateChecksum(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const result = e.target?.result as ArrayBuffer;
          const wordArray = CryptoJS.lib.WordArray.create(result);
          const hash = CryptoJS.MD5(wordArray).toString();
          resolve(hash);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(blob);
    });
  }

  /**
   * Divide o blob em chunks do tamanho especificado
   */
  private createChunks(blob: Blob, chunkSize: number): Blob[] {
    const chunks: Blob[] = [];
    let start = 0;
    
    while (start < blob.size) {
      const end = Math.min(start + chunkSize, blob.size);
      chunks.push(blob.slice(start, end));
      start = end;
    }
    
    return chunks;
  }

  /**
   * Inicia o upload de um vídeo
   */
  public async uploadVideo(
    blob: Blob, 
    metadata: any, 
    onProgress?: (progress: UploadProgress) => void,
    correlationId?: string
  ): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      if (this.uploadInProgress) {
        toast.warn("Um upload já está em andamento. Aguarde a conclusão.");
        return { success: false, error: "Upload em andamento" };
      }

      this.uploadInProgress = true;
      
      // Calcular checksum para verificação de integridade
      const checksum = await this.calculateChecksum(blob);
      
      // Gerar ID único para o arquivo
      const fileId = `video_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      const fileName = `${fileId}.${this.getExtensionFromMimeType(blob.type)}`;
      
      // Preparar chunks
      const chunks = this.createChunks(blob, CHUNK_SIZE);
      
      // Metadados para o upload
      const uploadMetadata: VideoUploadMetadata = {
        fileId,
        fileName,
        mimeType: blob.type,
        totalSize: blob.size,
        totalChunks: chunks.length,
        checksum
      };
      
      // Iniciar o upload registrando os metadados
      const initResponse = await this.initializeUpload(uploadMetadata);
      
      if (!initResponse.success) {
        this.uploadInProgress = false;
        
        // Adicionar à fila para tentar novamente mais tarde
        this.addToQueue(fileId, blob, metadata);
        
        return { 
          success: false, 
          error: initResponse.error || "Erro ao inicializar upload" 
        };
      }
      
      // Upload de cada chunk individualmente
      let uploadedSize = 0;
      let currentChunk = 0;
      
      for (const chunk of chunks) {
        // Atualizar progresso
        if (onProgress) {
          onProgress({
            totalBytes: blob.size,
            uploadedBytes: uploadedSize,
            percentage: (uploadedSize / blob.size) * 100,
            currentChunk,
            totalChunks: chunks.length
          });
        }
        
        // Enviar o chunk
        const chunkResponse = await this.uploadChunk(
          fileId, 
          chunk, 
          currentChunk, 
          chunks.length,
          correlationId
        );
        
        if (!chunkResponse.success) {
          // Salvar o progresso atual para retomar depois
          this.uploadInProgress = false;
          
          // Adicionar à fila para retomar mais tarde
          this.addToQueue(fileId, blob, metadata, currentChunk);
          
          return { 
            success: false, 
            error: chunkResponse.error || "Erro ao enviar chunk" 
          };
        }
        
        uploadedSize += chunk.size;
        currentChunk++;
      }
      
      // Finalizar o upload
      const completeResponse = await this.completeUpload(fileId, checksum);
      this.uploadInProgress = false;
      
      if (!completeResponse.success) {
        this.addToQueue(fileId, blob, metadata);
        return { 
          success: false, 
          error: completeResponse.error || "Erro ao finalizar upload" 
        };
      }
      
      TelemetryService.info(
        "VideoUploadService",
        "upload_complete" as EventType,
        { 
          fileId, 
          size: blob.size, 
          mimeType: blob.type,
          chunks: chunks.length
        },
        correlationId
      );
      
      return { success: true, data: completeResponse.data };
      
    } catch (error) {
      this.uploadInProgress = false;
      
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      TelemetryService.error(
        "VideoUploadService",
        "upload_error" as EventType,
        { error: errorMessage },
        correlationId
      );
      
      toast.error("Erro ao fazer upload do vídeo. Tentaremos novamente mais tarde.");
      
      // Adicionar à fila para tentar novamente mais tarde
      this.addToQueue(`video_${Date.now()}`, blob, metadata);
      
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Adiciona um vídeo à fila de upload
   */
  private addToQueue(fileId: string, blob: Blob, metadata: any, lastChunkUploaded: number = 0) {
    this.uploadQueue.set(fileId, { 
      blob, 
      metadata: {
        ...metadata,
        lastChunkUploaded
      }, 
      retries: 0 
    });
    
    // Se estiver online, tentar processar a fila
    if (navigator.onLine && !this.isProcessingQueue) {
      this.processQueue();
    }
  }

  /**
   * Processa a fila de upload
   */
  private async processQueue() {
    if (this.isProcessingQueue || this.uploadInProgress || !navigator.onLine || this.uploadQueue.size === 0) {
      return;
    }
    
    this.isProcessingQueue = true;
    
    // Converter Map entries para Array para evitar problemas de iteração
    const queueEntries = Array.from(this.uploadQueue.entries());
    
    for (const [fileId, { blob, metadata, retries }] of queueEntries) {
      if (retries >= 5) {
        // Limite de tentativas atingido, remover da fila
        this.uploadQueue.delete(fileId);
        continue;
      }
      
      try {
        // Atualizar contagem de tentativas
        this.uploadQueue.set(fileId, { blob, metadata, retries: retries + 1 });
        
        // Tentar upload
        const result = await this.uploadVideo(blob, metadata);
        
        if (result.success) {
          // Upload concluído com sucesso, remover da fila
          this.uploadQueue.delete(fileId);
          toast.success("Upload concluído com sucesso!");
        }
      } catch (error) {
        console.error("Erro ao processar fila de upload:", error);
      }
    }
    
    this.isProcessingQueue = false;
  }

  /**
   * Inicializa o upload registrando os metadados no servidor
   */
  private async initializeUpload(metadata: VideoUploadMetadata): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await axios.post('/api/videos/initialize', metadata);
      return { success: true, data: response.data };
    } catch (error) {
      const axiosError = error as AxiosError;
      return { 
        success: false, 
        error: axiosError.response?.data || axiosError.message 
      };
    }
  }

  /**
   * Envia um chunk para o servidor
   */
  private async uploadChunk(
    fileId: string, 
    chunk: Blob, 
    chunkIndex: number, 
    totalChunks: number,
    correlationId?: string
  ): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const formData = new FormData();
      formData.append('fileId', fileId);
      formData.append('chunkIndex', chunkIndex.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('chunk', chunk);
      
      const config: AxiosRequestConfig = {
        timeout: 30000, // 30 segundos por chunk
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          // Monitorar progresso específico do chunk
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);
          
          TelemetryService.info(
            "VideoUploadService",
            "chunk_progress" as EventType,
            { 
              fileId, 
              chunkIndex, 
              progress: percentCompleted 
            },
            correlationId
          );
        }
      };
      
      const response = await axios.post('/api/videos/chunk', formData, config);
      return { success: true, data: response.data };
    } catch (error) {
      const axiosError = error as AxiosError;
      return { 
        success: false, 
        error: axiosError.response?.data || axiosError.message 
      };
    }
  }

  /**
   * Finaliza o upload no servidor
   */
  private async completeUpload(fileId: string, checksum: string): Promise<{ success: boolean; data?: any; error?: any }> {
    try {
      const response = await axios.post('/api/videos/complete', { fileId, checksum });
      return { success: true, data: response.data };
    } catch (error) {
      const axiosError = error as AxiosError;
      return { 
        success: false, 
        error: axiosError.response?.data || axiosError.message 
      };
    }
  }

  /**
   * Obtém a extensão do arquivo a partir do tipo MIME
   */
  private getExtensionFromMimeType(mimeType: string): string {
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('x-matroska')) return 'mkv';
    if (mimeType.includes('quicktime')) return 'mov';
    return 'mp4'; // default
  }
}

export default VideoUploadService.getInstance(); 